import { useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import DriveFileRenameOutlineOutlinedIcon from "@mui/icons-material/DriveFileRenameOutlineOutlined";
import ManageSearchOutlinedIcon from "@mui/icons-material/ManageSearchOutlined";

import AppDialog from "../General/Dialog/AppDialog";
import FileUploadBox from "../General/FileUploadBox";
import { useToast } from "../../context/ToastContext";
import { searchLegalFiles } from "../../services/LegalFileService";
import {
  getUploadableDocumentTypes,
  uploadLegalFileDocuments,
  uploadLegalFileDocumentsZip,
} from "../../services/LegalFileAttachmentService";

const MAX_FILES = 20;
const MAX_FILE_MB = 20;

const MATCH_MODES = [
  {
    value: "filename",
    label: "Por el nombre del archivo",
    hint: "El nombre de cada PDF tiene el número de expediente. Ej: «9756247 ABACA CARLOS HUMBERTO.pdf»",
    icon: <DriveFileRenameOutlineOutlinedIcon />,
  },
  {
    value: "content",
    label: "Por el contenido del PDF",
    hint: "Se lee cada PDF y se busca el número de expediente dentro del documento. Ej: «Expediente: 9756247». Es más lento.",
    icon: <ManageSearchOutlinedIcon />,
  },
];

const isPdf = (f) => f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf");

/**
 * Carga manual de documentos del expediente: uno por uno (PDFs de un
 * expediente) o masiva (ZIP de PDFs que se asignan solos a su expediente).
 * Si recibe `legalFile`, solo ofrece la carga para ese expediente.
 */
export default function UploadLegalFileDocumentDialog({ open, onClose, onDone, legalFile = null }) {
  const { showToast } = useToast();

  const [types, setTypes] = useState([]);
  const [typesLoaded, setTypesLoaded] = useState(false);
  const [mode, setMode] = useState("single");
  const [documentType, setDocumentType] = useState("");

  const [selectedLegalFile, setSelectedLegalFile] = useState(null);
  const [legalFileOptions, setLegalFileOptions] = useState([]);
  const [legalFileInput, setLegalFileInput] = useState("");
  const [searching, setSearching] = useState(false);
  const [files, setFiles] = useState([]);

  const [matchMode, setMatchMode] = useState("filename");
  const [zipFile, setZipFile] = useState(null);

  const [saving, setSaving] = useState(false);

  // PDFs que parecen ser de otro expediente: hay que confirmar para cargarlos.
  const [mismatch, setMismatch] = useState(null);

  useEffect(() => {
    if (!open) return;
    setMode("single");
    setDocumentType("");
    setSelectedLegalFile(legalFile);
    setLegalFileInput("");
    setFiles([]);
    setMatchMode("filename");
    setZipFile(null);
    setMismatch(null);

    getUploadableDocumentTypes()
      .then((res) => setTypes(res?.data?.data ?? []))
      .catch(() => setTypes([]))
      .finally(() => setTypesLoaded(true));
  }, [open, legalFile]);

  useEffect(() => {
    if (legalFile) return undefined;
    const term = legalFileInput.trim();
    if (term.length < 2) {
      setLegalFileOptions([]);
      return undefined;
    }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const res = await searchLegalFiles({ perPage: 8, search: term });
        setLegalFileOptions(res.data?.data || []);
      } catch {
        setLegalFileOptions([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [legalFileInput, legalFile]);

  const handleAddFiles = (e) => {
    const all = Array.from(e.target.files ?? []);
    e.target.value = "";

    const valid = all.filter((f) => isPdf(f) && f.size <= MAX_FILE_MB * 1024 * 1024);
    if (valid.length < all.length) {
      showToast(`Solo PDF de hasta ${MAX_FILE_MB} MB.`, "warning");
    }
    setFiles((prev) => [...prev, ...valid].slice(0, MAX_FILES));
  };

  useEffect(() => {
    setMismatch(null);
  }, [files, selectedLegalFile, documentType, mode]);

  const selectedTypeLabel = types.find((t) => t.document_type === documentType)?.label;

  const canSubmit =
    !saving &&
    !!documentType &&
    (mode === "single" ? !!selectedLegalFile && files.length > 0 : !!zipFile);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("document_type", documentType);

      let res;
      if (mode === "single") {
        formData.append("legal_file_id", selectedLegalFile.id);
        files.forEach((f) => formData.append("files[]", f));
        if (mismatch) formData.append("confirm_mismatch", "1");
        res = await uploadLegalFileDocuments(formData);
      } else {
        formData.append("match_mode", matchMode);
        formData.append("file", zipFile);
        res = await uploadLegalFileDocumentsZip(formData);
      }

      const warnings = res.data?.warnings ?? [];
      if (warnings.length > 0) {
        showToast(`${res.data?.message ?? "Carga realizada."} ${warnings.join(" ")}`, "warning");
      } else {
        showToast(res.data?.message ?? "Carga realizada.", "success");
      }
      onDone?.();
      onClose();
    } catch (err) {
      if (err?.response?.status === 409 && err.response.data?.mismatches) {
        setMismatch(err.response.data);
        return;
      }
      const errors = err?.response?.data?.errors;
      const first = errors ? Object.values(errors)[0] : null;
      showToast(
        (Array.isArray(first) ? first[0] : first) ?? err?.response?.data?.message ?? "Error al cargar los documentos.",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const noTypes = typesLoaded && types.length === 0;

  return (
    <AppDialog
      open={open}
      onClose={() => !saving && onClose()}
      title="Cargar documentos al expediente"
      maxWidth="sm"
      fullWidth
      actions={
        <>
          <Button onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button
            variant="contained"
            color={mismatch ? "warning" : "primary"}
            onClick={handleSubmit}
            disabled={!canSubmit}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <CloudUploadOutlinedIcon />}
            sx={{ fontWeight: 800 }}
          >
            {mode === "zip" ? "Procesar ZIP" : mismatch ? "Cargar igual" : "Cargar"}
          </Button>
        </>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 0.5 }}>
        {noTypes && (
          <Alert severity="info">
            No hay tipos de documento habilitados. Registralos en Configuración → Fuentes de Documentos,
            eligiendo «Documento cargado en el expediente».
          </Alert>
        )}

        {!legalFile && (
          <ToggleButtonGroup
            value={mode}
            exclusive
            fullWidth
            size="small"
            onChange={(_, v) => v && setMode(v)}
          >
            <ToggleButton value="single" sx={{ textTransform: "none", fontWeight: 700 }}>
              Un expediente
            </ToggleButton>
            <ToggleButton value="zip" sx={{ textTransform: "none", fontWeight: 700 }}>
              Varios expedientes (ZIP)
            </ToggleButton>
          </ToggleButtonGroup>
        )}

        <FormControl size="small" fullWidth disabled={noTypes}>
          <InputLabel>Tipo de documento *</InputLabel>
          <Select value={documentType} label="Tipo de documento *" onChange={(e) => setDocumentType(e.target.value)}>
            {types.map((t) => (
              <MenuItem key={t.document_type} value={t.document_type}>{t.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {mode === "single" ? (
          <>
            {legalFile ? (
              <TextField label="Expediente" size="small" value={`${legalFile.code} — ${legalFile.name ?? ""}`} disabled fullWidth />
            ) : (
              <Autocomplete
                options={legalFileOptions}
                value={selectedLegalFile}
                onChange={(_, v) => setSelectedLegalFile(v)}
                inputValue={legalFileInput}
                onInputChange={(_, v) => setLegalFileInput(v)}
                filterOptions={(x) => x}
                loading={searching}
                getOptionLabel={(o) => (o ? `${o.code} — ${o.name ?? ""}` : "")}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                noOptionsText={legalFileInput.trim().length < 2 ? "Escribí el código o la carátula" : "Sin resultados"}
                renderInput={(params) => <TextField {...params} label="Expediente *" size="small" />}
              />
            )}

            {mismatch && (
              <Alert severity="warning">
                <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Revisá: {mismatch.mismatches.length === 1 ? "el archivo parece" : "estos archivos parecen"} de otro expediente.
                </Typography>
                {mismatch.mismatches.map((m) => (
                  <Typography key={m.file} variant="body2" sx={{ wordBreak: "break-all" }}>
                    • «{m.file}» indica el expediente <b>{m.detected_code}</b>, no el {mismatch.selected_code}.
                  </Typography>
                ))}
                <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
                  Si es un error, quitá el archivo o cambiá el expediente. Si estás seguro, tocá «Cargar igual».
                </Typography>
              </Alert>
            )}

            <Box>
              <Button
                component="label"
                variant="outlined"
                size="small"
                startIcon={<AttachFileIcon />}
                disabled={files.length >= MAX_FILES}
                sx={{ borderRadius: 2, fontWeight: 800, textTransform: "none" }}
              >
                Elegir PDFs
                <input type="file" hidden multiple accept=".pdf,application/pdf" onChange={handleAddFiles} />
              </Button>
              <Typography variant="caption" sx={{ display: "block", mt: 0.5, opacity: 0.65 }}>
                Uno o varios PDF, hasta {MAX_FILE_MB} MB cada uno.
              </Typography>
              {files.map((f, idx) => (
                <Box key={`${f.name}-${idx}`} sx={{ display: "flex", alignItems: "center", gap: 0.8, py: 0.3 }}>
                  <AttachFileIcon fontSize="small" sx={{ color: "primary.main" }} />
                  <Typography variant="body2" sx={{ flex: 1, wordBreak: "break-all" }}>{f.name}</Typography>
                  <IconButton size="small" onClick={() => setFiles((prev) => prev.filter((_, i) => i !== idx))}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          </>
        ) : (
          <>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
                ¿Cómo se reconoce el expediente de cada PDF?
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {MATCH_MODES.map((m) => {
                  const selected = matchMode === m.value;
                  return (
                    <Box
                      key={m.value}
                      onClick={() => setMatchMode(m.value)}
                      sx={{
                        display: "flex", gap: 1.5, p: 1.5, borderRadius: 2, cursor: "pointer",
                        border: "1px solid",
                        borderColor: selected ? "primary.main" : "divider",
                        bgcolor: selected ? "action.selected" : "transparent",
                        "&:hover": { bgcolor: selected ? "action.selected" : "action.hover" },
                      }}
                    >
                      <Box sx={{ color: selected ? "primary.main" : "text.secondary", pt: 0.2 }}>{m.icon}</Box>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{m.label}</Typography>
                        <Typography variant="caption" sx={{ opacity: 0.7 }}>{m.hint}</Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>

            <FileUploadBox
              selectedFile={zipFile}
              onFileSelect={setZipFile}
              accept=".zip"
              placeholder="Seleccionar ZIP con los PDFs"
            />

            <Alert severity="info" variant="outlined">
              Se procesa en segundo plano. Cada PDF se carga como «{selectedTypeLabel ?? "tipo elegido"}» en su expediente;
              los que no se puedan reconocer te llegan en la notificación final.
            </Alert>
          </>
        )}
      </Box>
    </AppDialog>
  );
}
